import { useState, useCallback } from 'react';
import { Copy, Check, Users, MessageCircle, AlertCircle } from 'lucide-react';
import { useCreateInvite } from '@/hooks/useTeamStats';
import { Card, CardContent } from '@mustauto/ui/web';

interface InviteTeamStepProps {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export function InviteTeamStep({ onNext, onBack, onSkip }: InviteTeamStepProps) {
  const inviteMutation = useCreateInvite();
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = useCallback(async () => {
    setError(null);
    try {
      const result = await inviteMutation.mutateAsync('mechanic');
      setInviteUrl(result.url);
    } catch {
      // Falha nunca pode ficar silenciosa (Regra 11): mostra erro + permite retry.
      setError('Não foi possível gerar o link. Verifique a conexão e tente novamente.');
    }
  }, [inviteMutation]);

  const handleCopy = useCallback(async () => {
    if (!inviteUrl) return;
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [inviteUrl]);

  const whatsappMessage = encodeURIComponent(
    `Oi! Estou usando o MustAuto para gerenciar a oficina. ` +
      `Entra nesse link para se cadastrar como mecânico: ${inviteUrl}`,
  );

  const handleWhatsApp = useCallback(() => {
    window.open(`https://wa.me/?text=${whatsappMessage}`, '_blank');
  }, [whatsappMessage]);

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-950/40 mx-auto">
          <Users size={28} className="text-blue-700 dark:text-blue-300" />
        </div>

        <h2 className="text-xl font-bold text-center">Convide sua equipe</h2>
        <p className="mt-1 text-sm text-muted-foreground text-center">
          Gere um link de convite para seus mecânicos se cadastrarem no app.
        </p>

        <div className="mt-8 space-y-4">
          {!inviteUrl ? (
            <>
              <button
                onClick={handleGenerate}
                disabled={inviteMutation.isPending}
                className="w-full rounded-lg bg-primary px-4 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {inviteMutation.isPending ? 'Gerando...' : 'Gerar link de convite'}
              </button>
              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/20 dark:text-red-300">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Link display */}
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2.5">
                <span className="flex-1 truncate text-sm font-mono text-foreground">{inviteUrl}</span>
                <button
                  onClick={handleCopy}
                  className="shrink-0 rounded-md border border-border bg-background p-2 text-muted-foreground hover:text-foreground transition-colors"
                  title="Copiar"
                >
                  {copied ? <Check size={16} className="text-emerald-600 dark:text-emerald-400" /> : <Copy size={16} />}
                </button>
              </div>

              {/* WhatsApp */}
              <button
                onClick={handleWhatsApp}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors"
              >
                <MessageCircle size={16} />
                Enviar via WhatsApp
              </button>
            </>
          )}

          {/* Info card */}
          <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900/60 dark:bg-blue-950/20">
            <CardContent className="p-4">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                Ao abrir o link, o mecânico faz login pelo celular e já fica associado
                à sua oficina. Você pode gerenciar a equipe depois em{' '}
                <span className="font-medium">Configurações &gt; Vagas &amp; Permissões</span>.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Nav buttons */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={onBack}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Voltar
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onSkip}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Vou convidar depois
            </button>
            {inviteUrl && (
              <button
                onClick={onNext}
                className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Continuar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
